import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import PasswordInput from "@/components/PasswordInput";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";

const REMEMBER_KEY = "rf_remember_login";

function loadRemembered() {
  try {
    const raw = localStorage.getItem(REMEMBER_KEY);
    if (!raw) return null;
    const obj = JSON.parse(atob(raw));
    if (obj && typeof obj.email === "string" && typeof obj.password === "string") return obj;
  } catch (e) { /* ignore */ }
  return null;
}

function saveRemembered(email, password) {
  try {
    localStorage.setItem(REMEMBER_KEY, btoa(JSON.stringify({ email, password })));
  } catch (e) { /* ignore quota / private-mode */ }
}

function clearRemembered() {
  try { localStorage.removeItem(REMEMBER_KEY); } catch (e) { /* ignore */ }
}

export default function Login() {
  const nav = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  // Prefill on mount if the user previously ticked "Remember me".
  // The password field is filled but no automatic submit is performed —
  // the user must still click "Log in" themselves.
  useEffect(() => {
    const saved = loadRemembered();
    if (saved) {
      setEmail(saved.email);
      setPassword(saved.password);
      setRemember(true);
    }
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      const u = await login(email, password);
      if (remember) saveRemembered(email, password); else clearRemembered();
      toast.success(`Welcome back, ${u.name}`);
      nav(u.role === "admin" ? "/admin" : "/dashboard");
    } catch (e) {
      const msg = formatApiError(e?.response?.data?.detail) || e.message;
      setErr(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground grid lg:grid-cols-2">
      {/* Left panel */}
      <div className="hidden lg:flex flex-col justify-between bg-primary text-primary-foreground p-12 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20 grid-bg" />
        <Link to="/" className="relative flex items-center gap-2" data-testid="login-back-home">
          <div className="h-8 w-8 rounded-md bg-primary-foreground text-primary grid place-items-center font-heading font-bold">R</div>
          <span className="font-heading font-bold text-lg">RegistryFeed</span>
        </Link>
        <div className="relative">
          <h1 className="font-heading font-black text-4xl leading-tight tracking-tight">
            The freshest<br />company registry data<br />in India.
          </h1>
          <p className="mt-4 text-primary-foreground/80 max-w-md">
            Log in to your dashboard to view today's uploads, download filtered exports, and stay ahead of the market.
          </p>
        </div>
        <div className="relative text-xs text-primary-foreground/70 num tracking-wider">
          — GST • MCA • Every day
        </div>
      </div>

      {/* Right panel */}
      <div className="flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-md">
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-8" data-testid="login-back-link">
            <ArrowLeft className="h-4 w-4" /> Back
          </Link>
          <h2 className="font-heading font-bold text-3xl">Log in</h2>
          <p className="mt-2 text-sm text-muted-foreground">Access your data dashboard</p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" data-testid="login-email-input" autoComplete="email" />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <PasswordInput id="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" testid="login-password-input" autoComplete="current-password" />
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="remember-me"
                checked={remember}
                onCheckedChange={(v) => {
                  const next = v === true;
                  setRemember(next);
                  if (!next) clearRemembered();
                }}
                data-testid="login-remember-checkbox"
              />
              <Label htmlFor="remember-me" className="text-sm cursor-pointer select-none">Remember me</Label>
            </div>
            {err && <div className="text-sm text-destructive" data-testid="login-error">{err}</div>}
            <Button type="submit" className="w-full btn-shine" disabled={loading} data-testid="login-submit-btn">
              {loading ? "Logging in..." : "Log in"}
            </Button>
          </form>

          <div className="mt-6 text-sm text-muted-foreground text-center">
            New here?{" "}
            <Link to="/register" className="text-primary font-medium hover:underline" data-testid="login-goto-register">Create an account</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
